import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronRight, ArrowRight, Heart, Shield, Stethoscope, Home, Clock } from 'lucide-react';
import { whatWeDoItems } from '../data/mock';

const subPages = {
  sanctuary: {
    title: 'Sanctuary & Daily Care',
    description: 'Our natural-living philosophy ensures every horse can live as nature intended.',
    image: 'https://images.unsplash.com/photo-1505494978186-6cde6324454b?w=1200&auto=format&fit=crop',
    sections: [
      {
        title: 'Natural Living Philosophy',
        content: 'At The Grazing Trust, we believe horses thrive when they can live naturally. Our sanctuary provides appropriate herd environments where horses can form social bonds, graze freely, and have the space they need to be horses. We prioritise turnout and natural behaviours over confinement.'
      },
      {
        title: 'Daily Care Routines',
        content: 'Every horse receives individual attention from our dedicated team. Daily routines include health checks, appropriate feeding based on individual needs, hoof care, and monitoring of herd dynamics. We maintain detailed records for each horse in our care.'
      },
      {
        title: 'Facilities & Environment',
        content: 'Our facilities are designed with horse welfare as the priority. Ample pasture, appropriate shelter, safe fencing, and well-maintained grounds ensure a safe environment. We continuously assess and improve our facilities based on best practice.'
      }
    ]
  },
  rehabilitation: {
    title: 'Rehabilitation',
    description: 'Comprehensive care to restore physical and mental wellbeing.',
    image: 'https://images.unsplash.com/photo-1590839823121-c253bf8d1e7d?w=1200&auto=format&fit=crop',
    sections: [
      {
        title: 'Veterinary Care',
        content: 'Every horse entering our sanctuary receives a thorough veterinary assessment. We work with experienced equine veterinarians to address health issues, from dental care and parasite management to more complex medical conditions requiring ongoing treatment.'
      },
      {
        title: 'Physical Rehabilitation',
        content: 'Horses recovering from neglect, injury, or illness receive tailored rehabilitation programmes. This may include controlled exercise, physiotherapy, and careful nutritional management to help them regain strength and condition.'
      },
      {
        title: 'Behavioural Support',
        content: 'Many horses arrive with behavioural challenges stemming from past experiences. Our team uses patient, positive methods to rebuild trust and confidence. We work at each horse’s pace, recognising that mental recovery is as important as physical healing.'
      }
    ]
  },
  rehoming: {
    title: 'Rehoming & Loan Programme',
    description: 'Finding perfect matches for horses ready for a new chapter.',
    image: 'https://images.unsplash.com/photo-1692024424434-27d10abba669?w=1200&auto=format&fit=crop',
    sections: [
      {
        title: 'Our Approach',
        content: 'Not all horses in our care need permanent sanctuary – many are ready for loving homes. Our rehoming programme carefully matches horses with suitable carers through a thorough vetting process. We take time to ensure every match is right for both horse and human.'
      },
      {
        title: 'The Process',
        content: 'Interested adopters complete an application form and home visit assessment. We discuss the horse’s needs, history, and suitability. A settling-in period allows both parties to adjust, and our loan agreement ensures we can monitor the horse’s ongoing welfare.'
      },
      {
        title: 'Ongoing Support',
        content: 'Rehoming doesn’t end when a horse leaves us. We provide ongoing support to new carers, conduct regular welfare visits, and are always available for advice. If circumstances change, we guarantee a home back at the sanctuary.'
      }
    ],
    cta: { text: 'Apply to Rehome', link: '/rehoming-application' }
  },
  'lifelong-sanctuary': {
    title: 'Lifelong Sanctuary & End-of-Life Care',
    description: 'Permanent, compassionate care for horses who cannot be rehomed.',
    image: 'https://images.unsplash.com/photo-1656241281956-982c1af244f0?w=1200&auto=format&fit=crop',
    sections: [
      {
        title: 'Permanent Sanctuary',
        content: 'Some horses in our care will never leave – and that’s absolutely fine. Whether due to age, health conditions, behavioural challenges, or simply because they’ve found their forever herd with us, we provide permanent sanctuary for as long as they need.'
      },
      {
        title: 'Quality of Life',
        content: 'Sanctuary life doesn’t mean just existing. Our permanent residents enjoy rich lives with herd companions, appropriate enrichment, and individual care. We regularly assess quality of life to ensure every horse is comfortable and content.'
      },
      {
        title: 'Compassionate End-of-Life Care',
        content: 'When the time comes, we are committed to providing dignified, peaceful end-of-life care. Decisions are made with veterinary guidance, prioritising the horse’s comfort above all else. We believe in letting horses go with kindness when the time is right.'
      }
    ]
  }
};

const WhatWeDoPage = () => {
  const { subpage } = useParams();
  
  if (subpage && subPages[subpage]) {
    const page = subPages[subpage];
    return (
      <main>
        <div className="page-header">
          <div className="container">
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <ChevronRight size={16} />
              <Link to="/what-we-do">What We Do</Link>
              <ChevronRight size={16} />
              <span>{page.title}</span>
            </div>
            <h1 className="display-medium">{page.title}</h1>
            <p className="body-large">{page.description}</p>
          </div>
        </div>

        <section className="section">
          <div className="container">
            <img 
              src={page.image} 
              alt={page.title}
              style={{ 
                width: '100%', 
                height: '400px', 
                objectFit: 'cover', 
                borderRadius: '24px',
                marginBottom: '48px'
              }}
            />
            
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              {page.sections.map((section, index) => (
                <div key={index} style={{ marginBottom: '48px' }}>
                  <h2 className="heading-2" style={{ marginBottom: '16px' }}>{section.title}</h2>
                  <p className="body-medium">{section.content}</p>
                </div>
              ))}
              
              {page.cta && (
                <div style={{ textAlign: 'center', marginTop: '48px' }}>
                  <Link to={page.cta.link} className="btn-primary">
                    {page.cta.text}
                    <ArrowRight size={18} style={{ marginLeft: '8px' }} />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="container">
            <h2 className="display-small">Support Our Work</h2>
            <p className="body-large" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
              Your donations directly support the horses in our care.
            </p>
            <Link to="/donate" className="btn-cta">
              <Heart size={20} style={{ marginRight: '8px' }} />
              Donate Now
            </Link>
          </div>
        </section>
      </main>
    );
  }

  // Main What We Do overview page
  return (
    <main>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <span>What We Do</span>
          </div>
          <h1 className="display-medium">What We Do</h1>
          <p className="body-large">
            From rescue to rehabilitation, rehoming to lifelong care – supporting horses at every stage.
          </p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div className="grid-2">
            {whatWeDoItems.map((item) => (
              <Link 
                key={item.id} 
                to={item.link}
                className="network-card hover-lift"
                style={{ textDecoration: 'none', overflow: 'hidden' }}
              >
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="card-image"
                />
                <h3 className="heading-2" style={{ marginBottom: '12px' }}>{item.title}</h3>
                <p className="body-medium" style={{ marginBottom: '16px' }}>{item.description}</p>
                <span 
                  style={{ 
                    color: 'var(--brand-dark)', 
                    fontWeight: 600, 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '4px' 
                  }}
                >
                  Learn more <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-light">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Our Impact</h2>
            <p className="body-medium">
              Every horse that comes through our doors receives dedicated, professional care.
            </p>
          </div>
          <div className="grid-4">
            {[
              { icon: Home, label: 'Safe Refuge', desc: 'For horses at risk' },
              { icon: Stethoscope, label: 'Rehabilitation', desc: 'Physical & mental care' },
              { icon: Heart, label: 'Rehoming', desc: 'Carefully matched placements' },
              { icon: Clock, label: 'Lifelong Care', desc: 'For those who need it' }
            ].map((item, index) => (
              <div key={index} className="network-card" style={{ textAlign: 'center' }}>
                <item.icon size={32} color="var(--brand-dark)" style={{ marginBottom: '12px' }} />
                <h4 className="heading-3">{item.label}</h4>
                <p className="body-small">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2 className="display-small">Need Help for a Horse?</h2>
          <p className="body-large" style={{ maxWidth: '600px', margin: '0 auto 32px' }}>
            If you or someone you know can no longer care for a horse, we may be able to help.
          </p>
          <Link to="/help-a-horse" className="btn-cta">Get Help for a Horse</Link>
        </div>
      </section>
    </main>
  );
};

export default WhatWeDoPage;
